import React, { useContext, useMemo, useState } from 'react';
import { notification } from 'antd';

import StepResultsModal from '@totem/components/services/remoteAccess/auth0users/details/StepResultsModal';
import { MultiStepResults } from '@totem/components/services/remoteAccess/auth0users/details/types';
import NoSiteAccessUsersContext from '@totem/components/services/remoteAccess/v2/noSiteAccess/NoSiteAccessUsersContext';
import NoSiteAccessUsersSearchBar from '@totem/components/services/remoteAccess/v2/noSiteAccess/NoSiteAccessUsersSearchBar';
import NoSiteAccessUsersTable from '@totem/components/services/remoteAccess/v2/noSiteAccess/NoSiteAccessUsersTable';
import RemoveUsersDialog, {
  UserRoleRemoval,
} from '@totem/components/services/remoteAccess/v2/users/RemoveUsersDialog';
import {
  QRUserRole,
  RemoveUserFromRolesRequestItem,
} from '@totem/components/services/remoteAccess/v2/users/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_USERS_ROLES_REMOVE_ENDPOINT } from '@totem/utilities/endpoints';

const NoSiteAccessUsers = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { rows, selectedUserIds, setSelectedUserIds, onRefresh } = useContext(
    NoSiteAccessUsersContext,
  );
  const [removeTargets, setRemoveTargets] = useState<QRUserRole[] | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<MultiStepResults | null>(null);
  const [showResults, setShowResults] = useState<boolean>(false);

  const isInternal = permissions.internal.hasPermissions(userProfile);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedUserIds.includes(row.user.user_id)),
    [rows, selectedUserIds],
  );

  const handleConfirmRemove = (removals: UserRoleRemoval[]) => {
    if (confirming) {
      return;
    }
    setConfirming(true);

    const payload: RemoveUserFromRolesRequestItem[] = removals.map(
      (removal) => ({
        user: {
          id: removal.user.user.user_id,
          name: removal.user.user.name,
        },
        roles: removal.user.roles
          .filter((role) => removal.roleIds.includes(role.id))
          .map((role) => ({ id: role.id, name: role.name })),
      }),
    );

    fetch(AUTH0_USERS_ROLES_REMOVE_ENDPOINT, {
      method: 'PATCH',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((result: MultiStepResults) => {
        onRefresh();

        setSelectedUserIds([]);

        setRemoveTargets(null);

        if (isInternal) {
          setLastResult(result);

          setShowResults(true);

          return;
        }

        if (result?.success) {
          notification.success({
            message: 'Users removed',
            description: 'Removed the selected users from the chosen roles.',
          });
        } else {
          notification.error({
            message: 'Error removing users',
            description:
              result?.error ??
              'An error occurred while removing the selected users.',
            duration: 0,
          });
        }
      })
      .catch(() => {
        notification.error({
          message: 'Error removing users',
          description: 'An error occurred while removing the selected users.',
          duration: 0,
        });
      })
      .finally(() => setConfirming(false));
  };

  return (
    <>
      <NoSiteAccessUsersSearchBar
        onRemoveSelected={() => setRemoveTargets(selectedRows)}
      />
      <NoSiteAccessUsersTable onRemoveUser={(row) => setRemoveTargets([row])} />
      <RemoveUsersDialog
        visible={!!removeTargets}
        users={removeTargets ?? []}
        confirming={confirming}
        onConfirm={handleConfirmRemove}
        onClose={() => setRemoveTargets(null)}
      />
      <StepResultsModal
        visible={showResults}
        title="Remove Users Result"
        result={lastResult}
        successMessage="Users removed"
        failureMessage="Remove users failed"
        onClose={() => setShowResults(false)}
      />
    </>
  );
};

export default NoSiteAccessUsers;
